import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import type {
  Permission,
  ScopeKind,
} from "../../../../shared/rbac/permissions";
import {
  permissionsForScope,
  requiredReadPermission,
} from "../../../../shared/rbac/permissions";
import { playClick } from "../../../lib/sounds";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Text } from "../../Text";
import { TabbedModal } from "../../TabbedModal";
import { Toggle } from "../../Toggle";
import { Card, ColoredIconBadge, FieldLabel } from "../shared";
import { SCOPE_ICONS, SCOPE_LABELS } from "./constants";
import { PermissionsList } from "./PermissionsList";

type Props = {
  open: boolean;
  onClose: () => void;
  scopeKind: ScopeKind;
};

type Draft = { name: string; permissions: Set<Permission> };

function initialDraft(scopeKind: ScopeKind): Draft {
  const read = requiredReadPermission(scopeKind);
  return { name: "", permissions: new Set(read ? [read] : []) };
}

export function CreateRoleModal({ open, onClose, scopeKind }: Props) {
  const z = useZero();
  const [draft, setDraft] = useState(() => initialDraft(scopeKind));
  const requiredRead = requiredReadPermission(scopeKind);

  const scopePermissions = useMemo(
    () => permissionsForScope(scopeKind),
    [scopeKind]
  );

  const togglePermission = useCallback(
    (p: Permission) => {
      if (p === requiredRead) return;
      setDraft((prev) => {
        const next = new Set(prev.permissions);
        if (next.has(p)) next.delete(p);
        else next.add(p);
        return { ...prev, permissions: next };
      });
    },
    [requiredRead]
  );

  const handleClose = () => {
    setDraft(initialDraft(scopeKind));
    onClose();
  };

  const handleCreate = () => {
    const trimmed = draft.name.trim();
    if (!trimmed || draft.permissions.size === 0) return;
    z.mutate(
      sharedMutators.createRole({
        id: nanoid(12),
        name: trimmed,
        scopeKind,
        permissions: [...draft.permissions],
      })
    );
    handleClose();
  };

  const handleRowClick = useCallback(
    (p: Permission) => {
      if (p === requiredRead) return;
      playClick();
      togglePermission(p);
    },
    [requiredRead, togglePermission]
  );

  const isInteractive = useCallback(
    (p: Permission) => p !== requiredRead,
    [requiredRead]
  );

  if (!open) return null;

  return (
    <TabbedModal
      icon={
        <ColoredIconBadge id={scopeKind} iconName={SCOPE_ICONS[scopeKind]} />
      }
      title={`New role for ${SCOPE_LABELS[scopeKind].toLowerCase()}`}
      onClose={handleClose}
      footerRight={
        <>
          <Button size="l" variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="l"
            onClick={handleCreate}
            disabled={!draft.name.trim() || draft.permissions.size === 0}
          >
            Create
          </Button>
        </>
      }
    >
      <Card>
        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Name</Text>
          </Box>
          <Input
            autoFocus
            placeholder="e.g. Space commenter"
            value={draft.name}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, name: e.target.value }))
            }
            style={{ minWidth: 0, width: "100%" }}
          />
        </FieldLabel>

        <PermissionsList
          permissions={scopePermissions}
          onRowClick={handleRowClick}
          isInteractive={isInteractive}
          renderTrailing={(p) => (
            <div onClick={(e) => e.stopPropagation()}>
              <Toggle
                isOn={draft.permissions.has(p)}
                onToggle={() => togglePermission(p)}
                disabled={p === requiredRead}
              />
            </div>
          )}
        />
      </Card>
    </TabbedModal>
  );
}
